import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useNavigate, useParams, Link, useSearchParams } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, ArrowRight, Mail } from 'lucide-react';
import googleLogo from '@/assets/brands/google.svg';
import microsoftLogo from '@/assets/brands/microsoft.svg';
const Auth = () => {
  const { t } = useTranslation('common');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [showEmailSignIn, setShowEmailSignIn] = useState(false);
  const [activeTab, setActiveTab] = useState<'signup' | 'signin'>('signup');
  const [oauthLoadingProvider, setOauthLoadingProvider] = useState<'google' | 'azure' | null>(null);
  const {
    signIn,
    signUp,
    user
  } = useAuth();
  const {
    toast
  } = useToast();
  const navigate = useNavigate();
  const {
    lang
  } = useParams<{
    lang?: string;
  }>();
  const [searchParams] = useSearchParams();
  const prefix = `/${lang || 'en'}`;
  
  // Reset password state
  const [resetEmail, setResetEmail] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  
  // New password state
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordUpdateSuccess, setPasswordUpdateSuccess] = useState(false);
  
  const mode = searchParams.get('mode');

  const microsoftSSOIsEnabled = false;
  const googleSSOIsEnabled = true;  

  useEffect(() => {
    if (user) {
      navigate(prefix);
    }
  }, [user, navigate, prefix]);

  useEffect(() => {
    // Handle tab query parameter
    const tabParam = searchParams.get('tab');
    if (tabParam === 'signin' || tabParam === 'signup') {
      setActiveTab(tabParam);
    }
  }, [searchParams]);
  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      toast({
        title: 'Missing fields',
        description: 'Please enter your email and password.',
        variant: 'destructive'
      });
      return;
    }
    setIsLoading(true);
    try {
      const {
        error
      } = await signIn(email, password);
      if (error) {
        if (error.message.includes('Invalid login credentials')) {
          toast({
            title: 'Login failed',
            description: 'Invalid email or password. Please try again.',
            variant: 'destructive'
          });
        } else {
          toast({
            title: 'Error',
            description: error.message,
            variant: 'destructive'
          });
        }
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'An unexpected error occurred. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };
  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!displayName || !email || !password) {
      toast({
        title: 'Missing fields',
        description: 'Please fill in name, email, and password.',
        variant: 'destructive'
      });
      return;
    }
    if (password.length < 6) {
      toast({
        title: 'Weak password',
        description: 'Password must be at least 6 characters.',
        variant: 'destructive'
      });
      return;
    }
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email)) {
      toast({
        title: 'Invalid email',
        description: 'Please enter a valid email address.',
        variant: 'destructive'
      });
      return;
    }
    setIsLoading(true);
    try {
      const {
        error
      } = await signUp(email, password, displayName);
      if (error) {
        if (error.message.includes('User already registered')) {
          toast({
            title: 'Account exists',
            description: 'An account with this email already exists. Please sign in instead.',
            variant: 'destructive'
          });
        } else {
          toast({
            title: 'Error',
            description: error.message,
            variant: 'destructive'
          });
        }
      } else {
        toast({
          title: 'Success!',
          description: 'Account created successfully. Please check your email to verify your account.'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'An unexpected error occurred. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail) {
      toast({
        title: t('auth.resetPassword.errorTitle'),
        description: 'Please enter your email address.',
        variant: 'destructive'
      });
      return;
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(resetEmail)) {
      toast({
        title: t('auth.resetPassword.errorTitle'),
        description: 'Please enter a valid email address.',
        variant: 'destructive'
      });
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `https://exhub.jigso.com/en/auth?mode=recovery`
      });
      
      if (error) {
        toast({
          title: t('auth.resetPassword.errorTitle'),
          description: t('auth.resetPassword.errorMessage'),
          variant: 'destructive'
        });
      } else {
        setResetSuccess(true);
        toast({
          title: t('auth.resetPassword.successTitle'),
          description: t('auth.resetPassword.successMessage')
        });
      }
    } catch (error) {
      toast({
        title: t('auth.resetPassword.errorTitle'),
        description: t('auth.resetPassword.errorMessage'),
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newPassword || !confirmPassword) {
      toast({
        title: t('auth.setNewPassword.errorTitle'),
        description: 'Please enter and confirm your new password.',
        variant: 'destructive'
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast({
        title: t('auth.setNewPassword.errorTitle'),
        description: t('auth.setNewPassword.passwordMismatch'),
        variant: 'destructive'
      });
      return;
    }

    if (newPassword.length < 6) {
      toast({
        title: t('auth.setNewPassword.errorTitle'),
        description: t('auth.setNewPassword.passwordTooShort'),
        variant: 'destructive'
      });
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });
      
      if (error) {
        toast({
          title: t('auth.setNewPassword.errorTitle'),
          description: t('auth.setNewPassword.errorMessage'),
          variant: 'destructive'
        });
      } else {
        setPasswordUpdateSuccess(true);
        toast({
          title: t('auth.setNewPassword.successTitle'),
          description: t('auth.setNewPassword.successMessage')
        });
        // Redirect to home after successful password update
        setTimeout(() => {
          window.location.href = prefix;
        }, 2000);
      }
    } catch (error) {
      toast({
        title: t('auth.setNewPassword.errorTitle'),
        description: t('auth.setNewPassword.errorMessage'),
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOAuth = async (provider: 'google' | 'azure') => {
    setOauthLoadingProvider(provider);
    setIsLoading(true);
    try {
      const {
        error
      } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}${prefix}`
        }
      });
      if (error) {
        toast({
          title: 'Error',
          description: error.message,
          variant: 'destructive'
        });
      }
    } catch {
      toast({
        title: 'Error',
        description: 'Unable to start social login.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
      setOauthLoadingProvider(null);
    }
  };
  return <main className="min-h-screen bg-gradient-to-br from-background to-muted">
      <Helmet>
        <title>Create your free JiGSO account | JiGSO EX Hub</title>
        <meta name="description" content="Join JiGSO EX Hub in under 60 seconds. Save surveys, access past results, and get personalized recommendations." />
        <link rel="canonical" href={`${window.location.origin}${prefix}/auth`} />
      </Helmet>

      <section className="w-full">
        <div className="grid md:grid-cols-2 min-h-screen">
            {/* Left: Welcome + Benefits + Illustration */}
            <div className="order-1 md:order-none hidden md:block h-full w-full md:p-3">
              <div className="h-full w-full rounded-2xl border bg-gradient-to-br from-primary/15 via-pink-100/40 to-blue-100/40">
                <div className="h-full w-full flex flex-col justify-center p-8 md:p-10">
                  <div className="w-full max-w-xl mx-auto space-y-6">
                    <header>
                      <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
                        {t('auth.welcome')}
                      </h1>
                      <h2 className="text-2xl md:text-3xl font-bold tracking-tight">
                        {t('auth.subtitle')}
                      </h2>
                      <p className="mt-3 text-muted-foreground max-w-prose">
                        {t('auth.description')}
                      </p>
                    </header>

                    <ul className="grid gap-3">
                      <li className="flex items-start gap-3">
                        <ArrowRight className="h-5 w-5 text-primary mt-0.5" />
                        <span>{t('auth.benefits.bullet1')}</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <ArrowRight className="h-5 w-5 text-primary mt-0.5" />
                        <span>{t('auth.benefits.bullet2')}</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <ArrowRight className="h-5 w-5 text-primary mt-0.5" />
                        <span>{t('auth.benefits.bullet3')}</span>
                      </li>
                    </ul>

                    {/* Friendly illustration using iconography */}
                    
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Auth Card */}
            <div className="flex items-center justify-center p-6 md:p-12 bg-gradient-to-br from-primary/15 via-pink-100/40 to-blue-100/40 md:bg-none md:bg-transparent">
              <Card className="w-full max-w-lg mx-auto shadow-md bg-background">
                <CardHeader className="text-center">
                  <CardTitle className="text-2xl font-bold">
                    {mode === 'forgot' 
                      ? t('auth.resetPassword.title')
                      : mode === 'recovery'
                        ? t('auth.setNewPassword.title')
                      : activeTab === 'signup' 
                        ? t('auth.signup.title') 
                        : t('auth.signin.title')
                    }
                  </CardTitle>
                  <CardDescription>
                    {mode === 'forgot' 
                      ? t('auth.resetPassword.subtitle')
                      : mode === 'recovery'
                        ? t('auth.setNewPassword.subtitle')
                      : activeTab === 'signup' 
                        ? t('auth.signup.subtitle') 
                        : t('auth.signin.subtitle')
                    }
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {mode === 'forgot' ? (
                    // Forgot Password Form
                    <div className="space-y-4">
                      {!resetSuccess ? (
                        <form onSubmit={handleForgotPassword} className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="reset-email">{t('auth.resetPassword.fields.email')}</Label>
                            <Input 
                              id="reset-email" 
                              type="email" 
                              placeholder="Enter your email address" 
                              value={resetEmail} 
                              onChange={e => setResetEmail(e.target.value)} 
                              required 
                            />
                          </div>
                          <Button type="submit" className="w-full h-11" disabled={isLoading || !resetEmail}>
                            {isLoading ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> 
                                {t('auth.resetPassword.sendingLink')}
                              </>
                            ) : (
                              t('auth.resetPassword.sendLink')
                            )}
                          </Button>
                        </form>
                      ) : (
                        <div className="text-center space-y-4">
                          <div className="w-12 h-12 mx-auto bg-primary/10 rounded-full flex items-center justify-center">
                            <Mail className="h-6 w-6 text-primary" />
                          </div>
                          <div>
                            <h3 className="font-semibold">{t('auth.resetPassword.successTitle')}</h3>
                            <p className="text-sm text-muted-foreground mt-1">
                              {t('auth.resetPassword.successMessage')}
                            </p>
                          </div>
                        </div>
                      )}
                      
                      <div className="text-center">
                        <Link 
                          to={`${prefix}/auth`} 
                          className="text-sm text-primary hover:underline"
                        >
                          {t('auth.resetPassword.backToSignIn')}
                        </Link>
                      </div>
                    </div>
                  ) : mode === 'recovery' ? (
                    // Set New Password Form
                    <div className="space-y-4">
                      {!passwordUpdateSuccess ? (
                        <form onSubmit={handleSetNewPassword} className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="new-password">{t('auth.setNewPassword.fields.password')}</Label>
                            <Input 
                              id="new-password" 
                              type="password" 
                              placeholder="Enter your new password" 
                              value={newPassword} 
                              onChange={e => setNewPassword(e.target.value)} 
                              minLength={6}
                              required 
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="confirm-password">{t('auth.setNewPassword.fields.confirmPassword')}</Label>
                            <Input 
                              id="confirm-password" 
                              type="password" 
                              placeholder="Confirm your new password" 
                              value={confirmPassword} 
                              onChange={e => setConfirmPassword(e.target.value)} 
                              minLength={6}
                              required 
                            />
                          </div>
                          <Button type="submit" className="w-full h-11" disabled={isLoading || !newPassword || !confirmPassword}>
                            {isLoading ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> 
                                {t('auth.setNewPassword.updatingPassword')}
                              </>
                            ) : (
                              t('auth.setNewPassword.updatePassword')
                            )}
                          </Button>
                        </form>
                      ) : (
                        <div className="text-center space-y-4">
                          <div className="w-12 h-12 mx-auto bg-primary/10 rounded-full flex items-center justify-center">
                            <ArrowRight className="h-6 w-6 text-primary" />
                          </div>
                          <div>
                            <h3 className="font-semibold">{t('auth.setNewPassword.successTitle')}</h3>
                            <p className="text-sm text-muted-foreground mt-1">
                              {t('auth.setNewPassword.successMessage')}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    // Regular Sign In/Sign Up Tabs
                    <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'signup' | 'signin')} className="w-full">
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="signup">{t('auth.tabs.signUp')}</TabsTrigger>
                      <TabsTrigger value="signin">{t('auth.tabs.signIn')}</TabsTrigger>
                    </TabsList>

                    {/* Sign Up */}
                    <TabsContent value="signup" className="space-y-4">
                      {!showEmailForm ? (
                        <>
                          <div className="grid gap-3">
                            <Button
                              type="button"
                              variant="outline"
                              disabled={isLoading || oauthLoadingProvider === 'google'}
                              onClick={() => handleOAuth('google')}
                              className="w-full h-11 justify-center"
                            >
                              {oauthLoadingProvider === 'google' ? (
                                <>
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  {t('auth.signin.signingIn')}
                                </>
                              ) : (
                                <>
                                  <img src={googleLogo} alt="Google" className="mr-2 h-4 w-4" />
                                  {t('auth.continueWithGoogle')}
                                </>
                              )}
                            </Button>
                            {microsoftSSOIsEnabled && (
                            <Button
                              type="button"
                              variant="outline"
                              disabled={isLoading || oauthLoadingProvider === 'azure'}
                              onClick={() => handleOAuth('azure')}
                              className="w-full h-11 justify-center"
                            >
                              {oauthLoadingProvider === 'azure' ? (
                                <>
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  {t('auth.signin.signingIn')}
                                </>
                              ) : (
                                <>
                                  <img src={microsoftLogo} alt="Microsoft" className="mr-2 h-4 w-4" />
                                  {t('auth.continueWithMicrosoft')}
                                </>
                              )}
                            </Button>
                            )}
                          </div>
                          <div className="relative text-center">
                            <div className="absolute inset-0 flex items-center" aria-hidden="true">
                              <div className="w-full border-t" />
                            </div>
                            <span className="px-2 text-xs text-muted-foreground bg-card relative z-10">or</span>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setShowEmailForm(true)}
                            className="w-full h-11 justify-center"
                          >
                            <Mail className="mr-2 h-4 w-4" />
                            {t('auth.continueWithEmail')}
                          </Button>
                        </>
                      ) : (
                        <form onSubmit={handleSignUp} className="space-y-4">
                          <p className="text-xs text-center text-muted-foreground">
                            <button
                              type="button"
                              onClick={() => setShowEmailForm(false)}
                              className="underline underline-offset-2 hover:text-foreground"
                            >
                              {t('generic.back')}
                            </button>
                          </p>
                          <div className="space-y-2">
                            <Label htmlFor="signup-name">{t('auth.signup.fields.name')}</Label>
                            <Input id="signup-name" type="text" placeholder="Your name" value={displayName} onChange={e => setDisplayName(e.target.value)} required minLength={2} />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="signup-email">{t('auth.signup.fields.email')}</Label>
                            <Input id="signup-email" type="email" placeholder="you@company.com" value={email} onChange={e => setEmail(e.target.value)} required />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="signup-password">{t('auth.signup.fields.password')}</Label>
                            <Input id="signup-password" type="password" placeholder="Create a password" value={password} onChange={e => setPassword(e.target.value)} minLength={6} required />
                          </div>
                          <Button type="submit" className="w-full h-11 text-base shadow-md" disabled={isLoading || !displayName || !email || password.length < 6}>
                            {isLoading ? <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t('auth.signup.creatingAccount')}
                              </> : t('auth.signup.createAccount')}
                          </Button>
                        </form>
                      )}
                      
                      {/* Terms and Privacy Policy - Always visible */}
                      <div className="pt-2 space-y-2">
                        <p className="text-xs text-center text-muted-foreground">{t('auth.noSpam')}</p>
                        <p className="text-xs text-center text-muted-foreground">
                          <Trans
                            ns="common"
                            i18nKey="auth.byContinuing"
                            components={[
                              <Link to={`${prefix}/terms`} className="underline underline-offset-2 hover:text-foreground" />,
                              <Link to={`${prefix}/privacy`} className="underline underline-offset-2 hover:text-foreground" />
                            ]}
                          />
                        </p>
                      </div>
                    </TabsContent>

                    {/* Sign In */}
                    <TabsContent value="signin" className="space-y-4">
                      {!showEmailSignIn ? (
                        <>
                          <div className="grid gap-3">
                            <Button
                              type="button"
                              variant="outline"
                              disabled={isLoading || oauthLoadingProvider === 'google'}
                              onClick={() => handleOAuth('google')}
                              className="w-full h-11 justify-center"
                            >
                              {oauthLoadingProvider === 'google' ? (
                                <>
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  {t('auth.signin.signingIn')}
                                </>
                              ) : (
                                <>
                                  <img src={googleLogo} alt="Google" className="mr-2 h-4 w-4" />
                                  {t('auth.continueWithGoogle')}
                                </>
                              )}
                            </Button>
                            {microsoftSSOIsEnabled && (
                            <Button
                              type="button"
                              variant="outline"
                              disabled={isLoading || oauthLoadingProvider === 'azure'}
                              onClick={() => handleOAuth('azure')}
                              className="w-full h-11 justify-center"
                            >
                              {oauthLoadingProvider === 'azure' ? (
                                <>
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  {t('auth.signin.signingIn')}
                                </>
                              ) : (
                                <>
                                  <img src={microsoftLogo} alt="Microsoft" className="mr-2 h-4 w-4" />
                                  {t('auth.continueWithMicrosoft')}
                                </>
                              )}
                            </Button>
                            )}
                          </div>
                          <div className="relative text-center">
                            <div className="absolute inset-0 flex items-center" aria-hidden="true">
                              <div className="w-full border-t" />
                            </div>
                            <span className="px-2 text-xs text-muted-foreground bg-card relative z-10">or</span>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setShowEmailSignIn(true)}
                            className="w-full h-11 justify-center"
                          >
                            <Mail className="mr-2 h-4 w-4" />
                            {t('auth.continueWithEmail')}
                          </Button>
                        </>
                      ) : (
                        <form onSubmit={handleSignIn} className="space-y-4">
                          <p className="text-xs text-center text-muted-foreground">
                            <button
                              type="button"
                              onClick={() => setShowEmailSignIn(false)}
                              className="underline underline-offset-2 hover:text-foreground"
                            >
                              {t('generic.back')}
                            </button>
                          </p>
                          <div className="space-y-2">
                            <Label htmlFor="signin-email">{t('auth.signin.fields.email')}</Label>
                            <Input id="signin-email" type="email" placeholder="Enter your email" value={email} onChange={e => setEmail(e.target.value)} required />
                          </div>
                          <div className="flex justify-end">
                            <Link to={`${prefix}/auth?mode=forgot`} className="text-xs text-primary hover:underline">{t('auth.forgotPassword')}</Link>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="signin-password">{t('auth.signin.fields.password')}</Label>
                            <Input id="signin-password" type="password" placeholder="Enter your password" value={password} onChange={e => setPassword(e.target.value)} required />
                          </div>
                          <Button type="submit" className="w-full h-11" disabled={isLoading || !email || !password}>
                            {isLoading ? <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t('auth.signin.signingIn')}
                              </> : t('auth.signin.signIn')}
                          </Button>
                        </form>
                      )}
                    </TabsContent>
                  </Tabs>
                  )}
                </CardContent>
              </Card>
            </div>
        </div>
      </section>
    </main>;
};
export default Auth;